import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "./LeaveRequest.css";

function LeaveRequestForm({ employee }) {
  const employeeName = useMemo(() => employee?.name || "", [employee]);
  const employeeNo = useMemo(() => employee?.employeeNo || "", [employee]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leaveType, setLeaveType] = useState("Casual");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ✅ Fetch leave history
  useEffect(() => {
    if (!employeeName) return;
    axios
      .get(
        `https://hrms.urbango.co.in/server/index.php?endpoint=leave&employeeName=${encodeURIComponent(
          employeeName
        )}`
      )
      .then((res) => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHistory([]));
  }, [employeeName]);

  // ✅ Fetch leave balance info
  useEffect(() => {
    if (!employeeNo) return;
    axios
      .get(
        `https://hrms.urbango.co.in/server/index.php?endpoint=get_leave_balance&employeeNo=${encodeURIComponent(
          employeeNo
        )}`
      )
      .then((res) => {
        if (res.data) setLeaveBalance(res.data);
      })
      .catch(() => {
        console.warn("Unable to load leave balance");
      });
  }, [employeeNo]);

  // ✅ Form submit
  const isValidRange =
    startDate && endDate && new Date(startDate) <= new Date(endDate);
  const disableApply = submitting || !isValidRange || !reason || !employeeName;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disableApply) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        "https://hrms.urbango.co.in/server/index.php?endpoint=leave",
        {
          startDate,
          endDate,
          employee: employeeName,
          leaveType,
          reason,
        }
      );
      setStartDate("");
      setEndDate("");
      setReason("");
      setLeaveType("Casual");
      const res = await axios.get(
        `https://hrms.urbango.co.in/server/index.php?endpoint=leave&employeeName=${encodeURIComponent(
          employeeName
        )}`
      );
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Failed to submit leave. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCSV = () => {
    const header = ["Start Date", "End Date", "Employee", "Leave Type", "Reason", "Status"];
    const rows = history.map((r) => [
      r.startDate,
      r.endDate,
      r.employee || employeeName,
      r.leaveType,
      r.reason,
      r.status,
    ]);
    const csvContent = [header, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "leave_history.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="leave-root">
      <div className="leave-bg" aria-hidden="true" />

      <div className="leave-main-grid container-fluid">
        {/* Sidebar */}
        <aside className="leave-sidebar">
          <div className="sidebar-profile">
            <h4 className="sidebar-title">Leave Summary</h4>
            {leaveBalance ? (
              <>
                <div className="sidebar-card">
                  <p className="sidebar-label">Leave Avails</p>
                  <p className="sidebar-value">{leaveBalance.leave_avails}</p>
                </div>
                <div className="sidebar-card">
                  <p className="sidebar-label">Total Balance</p>
                  <p className="sidebar-value">{leaveBalance.total_leave_balance}</p>
                </div>
                <div className="sidebar-card">
                  <p className="sidebar-label">Leave Credit</p>
                  <p className="sidebar-value">{leaveBalance.leave_credit}</p>
                </div>
              </>
            ) : (
              <p className="text-muted small">Loading balance...</p>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <div className="leave-request-wrapper">
          <h2 className="leave-title">Apply Leave</h2>

          {error && <div className="alert alert-danger py-2 px-3 mb-3">{error}</div>}

          <form onSubmit={handleSubmit} className="leave-form">
            <div className="row g-3">
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label text-light small">Start date</label>
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="leave-input form-control"
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label text-light small">End date</label>
                <input
                  type="date"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="leave-input form-control"
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label text-light small">Leave type</label>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  className="leave-select form-select"
                >
                  <option value="Casual">Casual</option>
                  <option value="Sick">Sick</option>
                  <option value="Half-day">Half-day</option>
                  <option value="LOP">LOP</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>

              <div className="col-12 col-lg-3">
                <label className="form-label text-light small">Reason</label>
                <input
                  type="text"
                  required
                  placeholder="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="leave-input form-control"
                />
              </div>
            </div>

            {!isValidRange && (startDate || endDate) && (
              <div className="text-warning mt-2 small">
                End date must be on or after start date.
              </div>
            )}

            <div className="mt-3 d-grid d-sm-flex gap-2">
              <button type="submit" className="btn-submit" disabled={disableApply}>
                {submitting ? "Submitting..." : "Apply"}
              </button>
            </div>
          </form>

          <div className="d-flex justify-content-between align-items-center mt-4 mb-2 flex-wrap gap-2">
            <h2 className="leave-title mb-0">Leave History</h2>
            <button onClick={downloadCSV} className="btn-download">
              Download as CSV
            </button>
          </div>

          {/* Responsive Table */}
          <div className="table-responsive glass-table">
            <table className="leave-history-table table mb-0">
              <thead>
                <tr>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Leave Type</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="5">No records yet</td>
                  </tr>
                ) : (
                  history.map((r, idx) => (
                    <tr key={idx} className="history-row">
                      <td data-label="Start Date">{r.startDate}</td>
                      <td data-label="End Date">{r.endDate}</td>
                      <td data-label="Leave Type">{r.leaveType}</td>
                      <td data-label="Reason">{r.reason}</td>
                      <td
                        data-label="Status"
                        className={
                          r.status === "Approved"
                            ? "status-approved"
                            : r.status === "Pending"
                            ? "status-pending"
                            : "status-rejected"
                        }
                      >
                        {r.status}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeaveRequestForm;
